import { getPost, getTranslations, type BlogPost, type BlogPostSummary } from "./blog";
import { feedDiscoveryLinks } from "./feed";
import { getMessages } from "./i18n";
import { resolveLicense } from "./license";
import { LOCALES } from "./locale-registry";
import { pageSeo } from "./page-seo";
import { absoluteLocalizedUrl } from "./seo";
import { resolveSiteContext, sites, type Locale } from "./site";

const OG_LOCALE: Record<Locale, string> = Object.fromEntries(
  LOCALES.map((definition) => [definition.code, definition.ogLocale]),
) as Record<Locale, string>;

const PUBLISHER_LOGO = "https://inext.ltd/favicon.svg";

export function resolveBlogAssetUrl(assetPath: string, locale: Locale): string {
  if (/^https?:\/\//i.test(assetPath)) return assetPath;
  return new URL(assetPath, sites[locale].origin).href;
}

export function blogPostCanonicalUrl(post: Pick<BlogPost, "locale" | "slug">): string {
  return absoluteLocalizedUrl(`/blog/${post.slug}`, post.locale);
}

export function buildBlogArticleJsonLd(post: BlogPost | BlogPostSummary) {
  const canonical = blogPostCanonicalUrl(post);
  const image = post.heroImage ? resolveBlogAssetUrl(post.heroImage, post.locale) : undefined;
  const license = resolveLicense(post.license);

  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.seoDescription,
    datePublished: post.publishedAt,
    inLanguage: post.locale,
    isAccessibleForFree: true,
    ...(license.url ? { license: license.url } : {}),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonical,
    },
    author: {
      "@type": "Organization",
      name: "inext",
      url: sites.en.origin,
    },
    publisher: {
      "@type": "Organization",
      name: "inext",
      url: sites.en.origin,
      logo: {
        "@type": "ImageObject",
        url: PUBLISHER_LOGO,
      },
    },
    ...(image ? { image } : {}),
    url: canonical,
  };
}

export function blogPostArticleMeta(
  post: BlogPost | BlogPostSummary,
  canonical: string,
): Array<{ name?: string; property?: string; content: string }> {
  const meta: Array<{ name?: string; property?: string; content: string }> = [
    { property: "og:url", content: canonical },
    { property: "og:locale", content: OG_LOCALE[post.locale] },
    { property: "article:published_time", content: post.publishedAt },
  ];

  if (post.heroImage) {
    const image = resolveBlogAssetUrl(post.heroImage, post.locale);
    meta.push(
      { property: "og:image", content: image },
      { name: "twitter:image", content: image },
      { property: "og:image:alt", content: post.title },
    );
  }

  return meta;
}

export function blogIndexSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  const seo = pageSeo(locationHref, "/blog", messages.blogTitle, messages.blogDescription);
  return { ...seo, links: [...seo.links, ...feedDiscoveryLinks(site.locale)] };
}

export function blogPostSeo(locationHref: string, slug: string) {
  const site = resolveSiteContext(locationHref);
  const post = getPost(site, slug);
  if (!post) return blogIndexSeo(locationHref);

  const translations = getTranslations(post.storyId);
  const canonical = blogPostCanonicalUrl(post);
  const links: Array<{
    rel: string;
    hrefLang?: Locale | "x-default";
    type?: string;
    title?: string;
    href: string;
  }> = [
    { rel: "canonical", href: canonical },
    ...translations.map(({ locale, slug: translatedSlug }) => ({
      rel: "alternate",
      hrefLang: locale,
      href: absoluteLocalizedUrl(`/blog/${translatedSlug}`, locale),
    })),
    ...feedDiscoveryLinks(site.locale),
  ];
  const license = resolveLicense(post.license);
  if (license.url) {
    links.push({ rel: "license", href: license.url });
  }
  const english = translations.find(({ locale }) => locale === "en");
  if (english) {
    links.push({
      rel: "alternate",
      hrefLang: "x-default",
      href: absoluteLocalizedUrl(`/blog/${english.slug}`, "en"),
    });
  }

  return {
    meta: [
      { title: post.seoTitle },
      { name: "description", content: post.seoDescription },
      { property: "og:title", content: post.seoTitle },
      { property: "og:description", content: post.seoDescription },
      { property: "og:type", content: "article" },
      {
        name: "twitter:card",
        content: post.heroImage ? "summary_large_image" : "summary",
      },
      ...blogPostArticleMeta(post, canonical),
    ],
    links,
  };
}
